import React, {useContext, useEffect, useRef, useState} from 'react';
import {Card, CardActions, CardContent, Grid, Fab, CircularProgress, Box} from "@mui/material";
import uuid from 'react-uuid';
import {getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject} from "firebase/storage";
import Button from "@mui/material/Button";
import CustomMenuAppBar from "../menu-app-bar/CustomMenuAppBar";
import Typography from "@mui/material/Typography";
import {getAuth} from "firebase/auth";
import {doc, updateDoc, arrayUnion, arrayRemove} from "firebase/firestore";
import {db} from "../services/firebaseApp";
import Stack from "@mui/material/Stack";
import useCopyReact from "../services/useCopyReact";
import {AppContext} from "../services/AppContext";
import {toast, ToastContainer} from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from "react-router-dom";
import { 
  InsertDriveFile, 
  PictureAsPdf, 
  VideoFile, 
  AudioFile, 
  Description,
  Article,
  Code,
  FolderZip,
  Delete,
  CloudUpload
} from '@mui/icons-material';

const Dashboard = () => {
  const [productImg, setProductImg] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [copied, copyAction, setCopied] = useCopyReact("");
  const auth = getAuth()
  const {state: {currentUser}, dispatch} = useContext(AppContext);
  const navigate = useNavigate();
  const notify = (text) => toast(text);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/login');
    }
  }, [auth.currentUser, navigate]);

  // Return early if no user (prevents rendering before redirect)
  if (!auth.currentUser || !currentUser) {
    return null;
  }

  const copyText = (e) => {
    // setStringToCopy(e.currentTarget.getAttribute('url'))
    copyAction(e.currentTarget.getAttribute('url'));
    notify("Link copied to clipboard")
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <InsertDriveFile sx={{ fontSize: 80 }} />;
    
    if (fileType.startsWith('image/')) return null; // Show actual image
    if (fileType === 'application/pdf') return <PictureAsPdf sx={{ fontSize: 80, color: '#d32f2f' }} />;
    if (fileType.startsWith('video/')) return <VideoFile sx={{ fontSize: 80, color: '#1976d2' }} />;
    if (fileType.startsWith('audio/')) return <AudioFile sx={{ fontSize: 80, color: '#9c27b0' }} />;
    if (fileType.includes('word') || fileType.includes('document')) return <Description sx={{ fontSize: 80, color: '#2196f3' }} />;
    if (fileType.includes('sheet') || fileType.includes('excel')) return <Article sx={{ fontSize: 80, color: '#4caf50' }} />;
    if (fileType.includes('zip') || fileType.includes('rar') || fileType.includes('7z')) return <FolderZip sx={{ fontSize: 80, color: '#ff9800' }} />;
    if (fileType.includes('javascript') || fileType.includes('json') || fileType.includes('xml') || fileType.includes('html')) return <Code sx={{ fontSize: 80, color: '#607d8b' }} />;
    
    return <InsertDriveFile sx={{ fontSize: 80, color: '#757575' }} />;
  };

  const isImageFile = (fileType) => {
    return fileType && fileType.startsWith('image/');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  function getArtworkBucketLinkAndUpload(file) {
    if (!auth.currentUser) {
      notify("Please log in to upload files");
      return;
    }
    
    setProductImg(file)
    setIsUploading(true)
    setUploadProgress(0)
    
    try {
      const storage = getStorage();
      const storageRef = ref(storage, auth.currentUser.uid + '/' + uuid());

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed', 
        (snapshot) => {
          // Progress tracking
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        }, 
        (error) => {
          // Handle unsuccessful uploads
          console.log(error)
          setIsUploading(false)
          setProductImg(null)
          setUploadProgress(0)
          notify("Error uploading the file")
        }, 
        () => {
          // Handle successful uploads on complete
          getDownloadURL(uploadTask.snapshot.ref).then(async (downloadURL) => {
            try {
              console.log(auth.currentUser.uid)
              const userRef = doc(db, "users", auth.currentUser.uid);
              
              const fileData = {
                url: downloadURL,
                name: file.name,
                type: file.type,
                size: file.size,
                uploadedAt: new Date().toISOString()
              };
              
              await updateDoc(userRef, {
                files: arrayUnion(fileData)
              });
              
              if (currentUser) {
                const updatedUser = {...currentUser};
                if (!updatedUser.files) {
                  updatedUser.files = [];
                }
                updatedUser.files = [...updatedUser.files, fileData];
                
                // Keep backward compatibility
                if (!updatedUser.images) {
                  updatedUser.images = [];
                }
                updatedUser.images = [...updatedUser.images, downloadURL];
                
                dispatch({currentUser: updatedUser});
              }
              notify("upload complete")
              setIsUploading(false)
              setProductImg(null)
              setUploadProgress(0)
            } catch (e) {
              console.log(e)
              setIsUploading(false)
              setProductImg(null)
              setUploadProgress(0)
            }
          });
        }
      );
    } catch (e) {
      console.log(e)
      console.log("Error uploading the file")
      setIsUploading(false)
      setProductImg(null)
      setUploadProgress(0)
      notify("Error uploading the file")
    }
  }

  const deleteFile = async (fileToDelete) => {
    if (!auth.currentUser) {
      notify("Please log in to delete files");
      return;
    }
    
    setIsDeleting(true);
    try {
      const storage = getStorage();
      
      // Create storage reference from download URL
      const fileRef = ref(storage, fileToDelete.url);
      
      // Delete from Firebase Storage
      await deleteObject(fileRef);
      
      // Remove from Firestore
      const userRef = doc(db, "users", auth.currentUser.uid);
      await updateDoc(userRef, {
        files: arrayRemove(fileToDelete)
      });
      
      // Update local state
      if (currentUser) {
        const updatedFiles = currentUser.files.filter(file => file.url !== fileToDelete.url);
        const updatedUser = { ...currentUser, files: updatedFiles };
        
        // Also remove from images array for backward compatibility
        if (currentUser.images) {
          const updatedImages = currentUser.images.filter(imageUrl => imageUrl !== fileToDelete.url);
          updatedUser.images = updatedImages;
        }
        
        dispatch({ currentUser: updatedUser });
      }
      notify("File deleted successfully");
      
    } catch (error) {
      console.error("Error deleting file:", error);
      
      // Even if storage deletion fails, try to remove from Firestore
      try {
        if (!auth.currentUser) return;
        const userRef = doc(db, "users", auth.currentUser.uid);
        await updateDoc(userRef, {
          files: arrayRemove(fileToDelete)
        });
        
        // Update local state
        if (currentUser) {
          const updatedFiles = currentUser.files.filter(file => file.url !== fileToDelete.url);
          const updatedUser = { ...currentUser, files: updatedFiles };
          
          if (currentUser.images) {
            const updatedImages = currentUser.images.filter(imageUrl => imageUrl !== fileToDelete.url);
            updatedUser.images = updatedImages;
          }
          
          dispatch({ currentUser: updatedUser });
        }
        notify("File removed from list (storage deletion may have failed)");
      } catch (firestoreError) {
        notify("Error deleting file");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  const deleteLegacyImage = async (imageUrl) => {
    if (!auth.currentUser) {
      notify("Please log in to delete images");
      return;
    }
    
    setIsDeleting(true);
    try {
      const storage = getStorage();
      
      // Create storage reference from download URL
      const fileRef = ref(storage, imageUrl);
      
      // Delete from Firebase Storage
      await deleteObject(fileRef);
      
      // Remove from Firestore images array
      const userRef = doc(db, "users", auth.currentUser.uid);
      await updateDoc(userRef, {
        images: arrayRemove(imageUrl)
      });
      
      // Update local state
      if (currentUser) {
        const updatedImages = currentUser.images.filter(url => url !== imageUrl);
        const updatedUser = { ...currentUser, images: updatedImages };
        
        dispatch({ currentUser: updatedUser });
      }
      notify("Image deleted successfully");
      
    } catch (error) {
      console.error("Error deleting image:", error);
      
      // Even if storage deletion fails, try to remove from Firestore
      try {
        if (!auth.currentUser) return;
        const userRef = doc(db, "users", auth.currentUser.uid);
        await updateDoc(userRef, {
          images: arrayRemove(imageUrl)
        });
        
        // Update local state
        if (currentUser) {
          const updatedImages = currentUser.images.filter(url => url !== imageUrl);
          const updatedUser = { ...currentUser, images: updatedImages };
          
          dispatch({ currentUser: updatedUser });
        }
        notify("Image removed from list (storage deletion may have failed)");
      } catch (firestoreError) {
        notify("Error deleting image");
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div>
      <CustomMenuAppBar />
      <Grid container direction={"column"} alignContent={"center"} alignItems={"center"} justify={"center"}
            sx={{
              marginTop: 8,
              marginBottom: 4,
              px: 3
            }}>
        <Grid item sx={{textAlign: 'center', mb: 6}}>
          <Typography 
            variant={"h2"} 
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b8cf8 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              fontWeight: 700
            }}
          >
            Your Files
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            Manage and organize all your uploaded files in one secure location
          </Typography>
        </Grid>
      </Grid>

      <Grid container sx={{
          padding: { xs: 2, md: 4 },
          maxWidth: 1400,
          mx: 'auto',
          gap: 3
        }}>
        {currentUser && currentUser.files && currentUser.files.length > 0 ? (
          currentUser.files.map((file, index) => {
            const isImage = isImageFile(file.type);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={file.url} sx={{ display: 'flex' }}>
                <Card sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                  }
                }}>
                  <CardContent sx={{
                    flexGrow: 1,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      width: '100%',
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 16,
                      borderRadius: 12,
                      background: isImage ? 'transparent' : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      border: isImage ? 'none' : '2px dashed #cbd5e1',
                      overflow: 'hidden'
                    }}>
                      {isImage ? (
                        <img 
                          src={file.url} 
                          alt={file.name} 
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 12
                          }}
                        />
                      ) : (
                        getFileIcon(file.type)
                      )}
                    </div>
                    
                    <Typography 
                      variant="h6" 
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }} 
                      title={file.name}
                    >
                      {file.name}
                    </Typography>
                    
                    <Typography 
                      variant="caption" 
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1'
                      }}
                    >
                      {formatFileSize(file.size)}
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ 
                    p: 2,
                    pt: 0,
                    gap: 1,
                    flexDirection: 'column'
                  }}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      size="small"
                      url={file.url} 
                      onClick={(e) => copyText(e)}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        }
                      }}
                    >
                      Copy Link
                    </Button>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      size="small" 
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => deleteFile(file)}
                      disabled={isDeleting}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        fontWeight: 600,
                        borderWidth: '1.5px',
                        '&:hover': {
                          borderWidth: '1.5px',
                          backgroundColor: 'rgba(239, 68, 68, 0.05)',
                        }
                      }}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          })
        ) : (
          // Fallback for backward compatibility with old image-only data
          currentUser && currentUser.images && currentUser.images.map(imageUrl => {
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={imageUrl} sx={{ display: 'flex' }}>
                <Card sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                  }
                }}>
                  <CardContent sx={{
                    flexGrow: 1,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      width: '100%',
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 16,
                      borderRadius: 12,
                      overflow: 'hidden'
                    }}>
                      <img 
                        src={imageUrl} 
                        alt={'Legacy Image'} 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 12
                        }}
                      />
                    </div>
                    
                    <Typography 
                      variant="h6" 
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        mb: 1
                      }}
                    >
                      Legacy Image
                    </Typography>
                    
                    <Typography 
                      variant="caption" 
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1'
                      }}
                    >
                      Image File
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ 
                    p: 2,
                    pt: 0,
                    gap: 1,
                    flexDirection: 'column'
                  }}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      size="small"
                      url={imageUrl} 
                      onClick={(e) => copyText(e)}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        }
                      }}
                    >
                      Copy Link
                    </Button>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      size="small" 
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => deleteLegacyImage(imageUrl)}
                      disabled={isDeleting}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        fontWeight: 600,
                        borderWidth: '1.5px',
                        '&:hover': {
                          borderWidth: '1.5px',
                          backgroundColor: 'rgba(239, 68, 68, 0.05)',
                        }
                      }}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          })
        )}
      </Grid>
      <ToastContainer position="top-center"
                      autoClose={5000}
                      hideProgressBar={false}
                      newestOnTop={false}
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      theme="light"/>
      
      {/* Floating Action Button for Upload */}
      <Box sx={{ position: 'relative' }}>
        <Fab
          color="primary"
          aria-label="upload"
          component="label"
          disabled={isUploading}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            background: isUploading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            width: 64,
            height: 64,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            '&:hover': {
              background: isUploading ? '#9ca3af' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              transform: 'scale(1.05)',
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {isUploading ? (
            <CircularProgress 
              size={24} 
              variant="determinate"
              value={uploadProgress}
              sx={{ color: 'white' }}
            />
          ) : (
            <CloudUpload sx={{ fontSize: 28 }} />
          )}
          <input 
            type="file" 
            hidden 
            onChange={(e) => e.target.files[0] && getArtworkBucketLinkAndUpload(e.target.files[0])} 
            key={'uploadFab'}
          />
        </Fab>
        {isUploading && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 108,
              right: 32,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              padding: '8px 16px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {Math.round(uploadProgress)}%
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default Dashboard;

